import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, Typography, useMediaQuery } from '@mui/material';
import { ZoomIn, ZoomOut, FitScreen, Fullscreen, FullscreenExit, Download } from '@mui/icons-material';

// Recebe a URL blob já obtida pela API autenticada. Sua liberação pertence ao painel Arquivos.
export default function VisualizadorArquivo({ preview, onClose }) {
    const mobile = useMediaQuery('(max-width: 900px)');
    const [expandido, setExpandido] = useState(false);
    const [escala, setEscala] = useState(null); // null = ajustar à área disponível
    const [natural, setNatural] = useState({ width: 0, height: 0 });
    const [area, setArea] = useState({ width: 0, height: 0 });
    const [erro, setErro] = useState(false);
    const viewport = useRef(null), drag = useRef(null);
    const imagem = preview.row.mimeType?.startsWith('image/');
    const full = mobile || expandido;
    useEffect(() => {
        const el = viewport.current;
        if (!el || !imagem) return undefined;
        const medir = () => setArea({ width: el.clientWidth, height: el.clientHeight });
        medir();
        const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(medir) : null;
        observer?.observe(el);
        window.addEventListener('resize', medir);
        return () => { observer?.disconnect(); window.removeEventListener('resize', medir); };
    }, [imagem, full]);
    const ajuste = natural.width && area.width
        ? Math.min((area.width - 16) / natural.width, (area.height - 16) / natural.height, 1) : 1;
    const atual = escala ?? Math.max(0.001, ajuste);
    function zoom(fator) {
        setEscala(Math.max(0.001, Math.min(8, atual * fator)));
    }
    function ajustar() {
        setEscala(null);
        if (viewport.current) { viewport.current.scrollLeft = 0; viewport.current.scrollTop = 0; }
    }
    function iniciarArrasto(e) {
        if (e.pointerType !== 'mouse' || e.button !== 0) return;
        drag.current = { x: e.clientX, y: e.clientY, left: e.currentTarget.scrollLeft, top: e.currentTarget.scrollTop };
        e.currentTarget.setPointerCapture?.(e.pointerId);
        e.preventDefault();
    }
    function mover(e) {
        if (!drag.current) return;
        e.currentTarget.scrollLeft = drag.current.left - (e.clientX - drag.current.x);
        e.currentTarget.scrollTop = drag.current.top - (e.clientY - drag.current.y);
    }
    return <Dialog open fullWidth maxWidth="xl" fullScreen={full} onClose={onClose}
        PaperProps={{ sx: { height: full ? '100%' : '90dvh', maxHeight: full ? '100%' : '90dvh' } }}>
        <DialogTitle sx={{ overflowWrap: 'anywhere', fontSize: '1rem', py: 1 }}>{preview.row.nomeOriginal}</DialogTitle>
        <Stack direction="row" alignItems="center" flexWrap="wrap" sx={{ px: 1, borderBottom: 1, borderColor: 'divider' }}>
            {imagem && <>
                <IconButton title="Diminuir zoom" aria-label="Diminuir zoom" disabled={!natural.width || erro} onClick={() => zoom(1 / 1.25)}><ZoomOut /></IconButton>
                <Typography variant="body2" aria-live="polite" sx={{ minWidth: 64, textAlign: 'center' }}>{Math.round(atual * 100)}%</Typography>
                <IconButton title="Ampliar para conferir leitura e lacre" aria-label="Ampliar imagem" disabled={!natural.width || erro || atual >= 8} onClick={() => zoom(1.25)}><ZoomIn /></IconButton>
                <IconButton title="Ajustar foto inteira à tela" aria-label="Ajustar à tela" onClick={ajustar}><FitScreen /></IconButton>
                <Button type="button" title="Exibir em tamanho original: um pixel da imagem por pixel CSS" disabled={!natural.width || erro} onClick={() => setEscala(1)}>100%</Button>
            </>}
            {!mobile && <IconButton title={expandido ? 'Restaurar janela' : 'Expandir visualizador'} aria-label="Alternar tamanho da janela" onClick={() => setExpandido(v => !v)}>
                {expandido ? <FullscreenExit /> : <Fullscreen />}
            </IconButton>}
            <IconButton component="a" href={preview.url} download={preview.row.nomeOriginal || 'arquivo'} title="Baixar arquivo original" aria-label="Baixar arquivo original"><Download /></IconButton>
            <Button component="a" href={preview.url} target="_blank" rel="noopener noreferrer" title="Abrir arquivo no visualizador do navegador">Outra aba</Button>
        </Stack>
        {imagem && <Typography variant="caption" sx={{ px: 2, py: 0.5 }}>Use + e − para ampliar. No celular, deslize para percorrer a foto; no computador, arraste ou use as barras de rolagem.</Typography>}
        <DialogContent sx={{ p: 0, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {imagem ? <Box ref={viewport} tabIndex={0} role="region" aria-label="Foto ampliável da medição"
                onPointerDown={iniciarArrasto} onPointerMove={mover} onPointerUp={() => { drag.current = null; }}
                onPointerCancel={() => { drag.current = null; }} onLostPointerCapture={() => { drag.current = null; }}
                onDoubleClick={() => escala === null ? setEscala(Math.min(8, atual * 2)) : ajustar()}
                sx={{ flex: 1, minHeight: 0, overflow: 'auto', bgcolor: '#202124', touchAction: 'pan-x pan-y pinch-zoom', cursor: 'grab' }}>
                {erro ? <Alert severity="error">Não foi possível exibir a imagem. Tente baixar ou abrir em outra aba.</Alert>
                    : <Box sx={{ width: natural.width ? natural.width * atual + 16 : '100%', height: natural.height ? natural.height * atual + 16 : '100%', minWidth: '100%', minHeight: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Box component="img" src={preview.url} alt={preview.row.nomeOriginal} draggable={false}
                            onLoad={e => setNatural({ width: e.target.naturalWidth, height: e.target.naturalHeight })}
                            onError={() => setErro(true)}
                            sx={{ display: 'block', flexShrink: 0, width: natural.width ? natural.width * atual : 'auto', height: natural.height ? natural.height * atual : 'auto', maxWidth: 'none', userSelect: 'none', visibility: natural.width ? 'visible' : 'hidden' }} />
                    </Box>}
            </Box> : <Box component="iframe" src={preview.url} title={preview.row.nomeOriginal} sx={{ width: '100%', flex: 1, border: 0 }} />}
        </DialogContent>
        <DialogActions><Button type="button" title="Fechar o visualizador" onClick={onClose}>Fechar</Button></DialogActions>
    </Dialog>;
}
